package org.example.project.client.model

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

@Serializable
enum class AccountStatus {
    PENDING,
    ACTIVE,
    SUSPENDED,
    DELETED;

    companion object {
        fun fromString(value: String): AccountStatus {
            return entries.firstOrNull { it.name.equals(value, ignoreCase = true) }
                ?: throw IllegalArgumentException("Unknown account status: $value")
        }
    }
}

@Serializable(with = ClientTypeSerializer::class)
enum class ClientType {
    WEB,
    ANDROID;

    companion object {
        fun fromString(value: String): ClientType {
            return when (value.trim().lowercase()) {
                "web" -> WEB
                "android" -> ANDROID
                else -> throw IllegalArgumentException("Unknown clientType: '$value'. Allowed: 'web', 'android'")
            }
        }
    }
}

object ClientTypeSerializer : KSerializer<ClientType> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("ClientType", PrimitiveKind.STRING)

    override fun serialize(encoder: Encoder, value: ClientType) {
        encoder.encodeString(value.name.lowercase())
    }

    override fun deserialize(decoder: Decoder): ClientType {
        return ClientType.fromString(decoder.decodeString())
    }
}

@Serializable
data class RegisterRequest(
    val email: String,
    val password: String,
    val displayName: String
) {
    override fun toString(): String =
        "RegisterRequest(email=$email, password=REDACTED, displayName=$displayName)"
}

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
    val clientType: ClientType
) {
    override fun toString(): String =
        "LoginRequest(email=$email, password=REDACTED, clientType=$clientType)"
}

@Serializable
data class UserProfileResponse(
    val id: String,
    val email: String?,
    val displayName: String,
    val status: AccountStatus,
    val roles: Set<String> = emptySet(),
    val createdAt: String
)

@Serializable
data class AuthResponseAndroid(
    val token: String,
    val expiresAt: String,
    val user: UserProfileResponse
) {
    override fun toString(): String =
        "AuthResponseAndroid(token=REDACTED, expiresAt=$expiresAt, user=$user)"
}

@Serializable
data class AuthResponseWeb(
    val csrfToken: String,
    val expiresAt: String,
    val user: UserProfileResponse
) {
    override fun toString(): String =
        "AuthResponseWeb(csrfToken=REDACTED, expiresAt=$expiresAt, user=$user)"
}

@Serializable
data class UpdateProfileRequest(
    val displayName: String
)

@Serializable
data class LogoutResponse(
    val status: String = "LOGGED_OUT"
)

@Serializable
data class ApiErrorResponse(
    val error: String,
    val message: String,
    val requestId: String
)
